import { ChangeEventHandler, FC, useContext, useMemo } from "react";
import { BasicState } from "@app/geography";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { GlobalConstants } from "@app/shared/constants";
import { debounce } from 'lodash';
import { formatDateTime } from "@app/shared/formatDateTime";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { UserContext } from "@app/user/UserContext";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { AdminUsersContext } from "@app/admin/contexts/AdminUsersContext";
import { UserStatus, toDisplayAuditType } from "@app/admin/user-administration/userModel";

interface UserInfoProps {
  clientId: number;
  states: BasicState[];
  submitting?: boolean; // this isn't going to work, I'm sure
  handleClientChange: ChangeEventHandler<HTMLSelectElement>;
  userExists: boolean;
  userIsQuestAdmin: boolean;
  resendWelcomeEmail: () => void;
}

export const UserInfo: FC<React.PropsWithChildren<UserInfoProps>> = (props: UserInfoProps) => {
  const user = useContext(UserContext);
  const adminUsersContext = useContext(AdminUsersContext);

  const noClient = !adminUsersContext.clientId || adminUsersContext.clientId <= 0;
  const deletedUser = adminUsersContext.status === UserStatus.DELETED;

  const clientOptions = useMemo(() => {
    if (props.userExists) {
      // the user already exists and the client cannot be changed
      return <option value={adminUsersContext.clientId} key={adminUsersContext.clientId}>{adminUsersContext.clientName}</option>;
    } else if (!props.userIsQuestAdmin) {
      // authenticated user is a client admin and can only make users for their own client
      return <option value={user.clientId} key={user.clientId}>{user.clientName}</option>;
    }
    // the authenticated user is a quest admin and the user doesn't exist, so any client can be chosen
    return [<option value="-1" key="-1" />, ...adminUsersContext.allClients.map((client) => <option value={client.clientId} key={client.clientId}>{client.name}</option>)];
  }, [adminUsersContext.clientName, adminUsersContext.clientId, adminUsersContext.allClients]);

  const statesOptions = useMemo(() => {
    return props.states.map((state) => <option value={state.abbreviation} key={state.abbreviation}>{state.name}</option>);
  }, [props.states]);

  const emailValidation = useMemo(() => {
    return (
      <span>
        Email domain must be one of the following:
        <ul className="text-start">
          {adminUsersContext.allowedDomains.map((d) => <li key={d}>{d}</li>)}
        </ul>
      </span>
    );
  }, [adminUsersContext.allowedDomains]);

  const informationBlock = props.userExists && (
    <>
      <div className="row">
        <div className="col-12">
          <span className="qes-text-input__label">User Status History</span>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          {adminUsersContext.auditTrail.length > 0 && (
            <ul>
              {adminUsersContext.auditTrail.map((auditItem) => <li key={auditItem.userAuditId}><b>{toDisplayAuditType(auditItem.auditType)}:</b> {formatDateTime(auditItem.auditDate)} by {auditItem.auditedByUserName} {!!auditItem.auditRemarks && (<>(Remarks: {auditItem.auditRemarks})</>)}</li>)}
            </ul>
          )}
          {adminUsersContext.auditTrail.length == 0 && (
            <ul>
              <li>No User Status History available.</li>
            </ul>
          )}
        </div>
      </div>
    </>
  );

  const emailAndUserBlock = (
    <>
      <div className="row mb-4">
        <QESSelect
          containerClassName="col-12"
          label="Client"
          onChange={props.handleClientChange}
          disabled={props.userExists || !props.userIsQuestAdmin}
          testId="user-creation__client"
          value={props.userIsQuestAdmin || props.userExists ? adminUsersContext.clientId : user.clientId}
        >
          {clientOptions}
        </QESSelect>
      </div>
      <div className="row">
        <QESTextInput
          className="text-lowercase"
          containerClassName="col-5"
          label="Email"
          value={adminUsersContext.email}
          onChange={(e) => { adminUsersContext.setEmail(e.target.value.toLowerCase()); debouncedEmailChangeHandler(e); }}
          disabled={noClient || deletedUser}
          testId="user-creation__email"
          required={adminUsersContext.requiredFields["email"]}
          isValid={adminUsersContext.validationObject["email"]}
          errorText="Email address must use one of the client's authorized domains, and cannot already be in use. If the Username looks like an email, it must exactly match this field."
          validationTooltipContent={emailValidation}
        />
      </div>
      <div className={props.userExists ? "row mb-1" : "row mb-4"}>
        <QESCheckbox
          disabled={noClient || deletedUser || !props.userExists}
          containerClassName="col-5"
          label="Use Email as Username"
          checked={adminUsersContext.userNameIsSameAsEmail}
          onChange={(e) => {
            adminUsersContext.setUserNameIsSameAsEmail(e.target.checked);
            //if (e.target.checked)
            //  adminUsersContext.setUserName(adminUsersContext.email);
          }}
          testId="user-creation__username_is_same_as_email"
        />
      </div>
    </>
  );

  const unconfirmedEmailBlock = (
    <div className="row mb-1">
      <div className="col-5">
        This email address is not confirmed.
        <QESButton qesType="secondary" disabled={props.submitting || noClient || deletedUser} qesTestId="user-creation__resend-welcome-email" onClick={props.resendWelcomeEmail}>Resend Welcome Email</QESButton>
      </div>
    </div>
  );

  const emailChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    adminUsersContext.checkEmailAvailability(e.target.value)
  }

  const debouncedEmailChangeHandler = useMemo(
    () => debounce(emailChangeHandler, 500), [adminUsersContext.email, adminUsersContext.userName]);


  const userNameChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    adminUsersContext.checkUserNameAvailability(e.target.value)
  }

  const debouncedUserNameChangeHandler = useMemo(
    () => debounce(userNameChangeHandler, 500), [adminUsersContext.email, adminUsersContext.userName]);

  const usernameField = (
    <>
      {!adminUsersContext.userNameIsSameAsEmail &&
        <div className="row mb-1">
          <QESTextInput
            containerClassName="col-5"
            label="Username"
            value={adminUsersContext.userName}
            onChange={(e) => { adminUsersContext.setUserName(e.target.value || adminUsersContext.email); debouncedUserNameChangeHandler(e); }}
            testId="user-creation__username"
            required={adminUsersContext.requiredFields["userName"]}
            isValid={adminUsersContext.validationObject["userName"]}
            errorText="Username cannot already be in use. If the Username looks like an email, it must exactly match the email field."
            disabled={noClient || deletedUser}

          />
        </div>}
    </>
  );

  const nameBlock = (
    <div className="row mb-4">
      <QESTextInput containerClassName="col-5" label="First Name" value={adminUsersContext.firstName} onChange={(e) => adminUsersContext.setFirstName(e.target.value)} testId="user-creation__first-name" required={adminUsersContext.requiredFields["firstName"]} isValid={adminUsersContext.validationObject["firstName"]} disabled={noClient || deletedUser} />

      <QESTextInput containerClassName="col-2" label="Middle Initial" value={adminUsersContext.middleInitial} onChange={(e) => adminUsersContext.setMiddleInitial(e.target.value)} testId="user-creation__middle-initial" disabled={noClient || deletedUser} />

      <QESTextInput containerClassName="col-5" label="Last Name" value={adminUsersContext.lastName} onChange={(e) => adminUsersContext.setLastName(e.target.value)} testId="user-creation__last-name" required={adminUsersContext.requiredFields["lastName"]} isValid={adminUsersContext.validationObject["lastName"]} disabled={noClient || deletedUser} />
    </div>
  );

  const titleAndJobRoleBlock = (

    <div className="row mb-4">
      <QESTextInput
        containerClassName="col-6"
        label="Job Title"
        value={adminUsersContext.jobTitle}
        onChange={(e) => adminUsersContext.setJobTitle(e.target.value)}
        testId="user-creation__jobTitle"
        required={adminUsersContext.requiredFields["jobTitle"]}
        isValid={adminUsersContext.validationObject["jobTitle"]}
        disabled={noClient || deletedUser}
      />
      <QESSelect
        containerClassName="col-6"
        label="Job Role"
        onChange={(e) => {
          const selectedJobRoleType = adminUsersContext.jobRoleTypes?.find(pt => pt.jobRoleId === parseInt(e.target.value));
          adminUsersContext.setJobRole(selectedJobRoleType?.jobRole || '');
          adminUsersContext.setJobRoleId(selectedJobRoleType?.jobRoleId);
        }}
        testId="user-creation__jobRole"
        value={adminUsersContext.jobRoleId?.toString() || ''}
        required={false}
        isValid={adminUsersContext.validationObject["jobole"]}
        disabled={noClient || deletedUser}
      >
      <option value=""></option>
        {adminUsersContext.jobRoleTypes?.map((jobRoleType) => (
          <option value={jobRoleType.jobRoleId} key={jobRoleType.jobRoleId}>
            {jobRoleType.jobRole}
          </option>
        ))}
      </QESSelect>
    </div>
  );

  const addressBlock = (
    <>
      <div className="row mb-1">
        <QESTextInput containerClassName="col-7" label="Address" value={adminUsersContext.address} onChange={(e) => adminUsersContext.setAddress(e.target.value)} testId="user-creation__address" disabled={noClient || deletedUser} />
      </div>
      <div className="row mb-4">
        <QESTextInput containerClassName="col-5" label="City" value={adminUsersContext.city} onChange={(e) => adminUsersContext.setCity(e.target.value)} testId="user-creation__city" disabled={noClient || deletedUser} />
        <QESSelect
          containerClassName="col-2"
          label="State"
          onChange={(e) => adminUsersContext.setState(e.target.value)}
          testId="user-creation__state"
          value={adminUsersContext.state}
          disabled={noClient || props.states.length === 0 || deletedUser}
        >
          {statesOptions}
        </QESSelect>
        <QESTextInput containerClassName="col-2" label="Zip Code" value={adminUsersContext.zip} onChange={(e) => adminUsersContext.setZip(e.target.value)} testId="user-creation__zip" disabled={noClient || deletedUser} />
      </div>
    </>
  );

  const phoneAndSfIdBlock = (
    <div className="row mb-4">
      <QESTextInput
        containerClassName="col-3"
        label="Phone"
        value={adminUsersContext.phoneNumber}
        onChange={(e) => adminUsersContext.setPhoneNumber(e.target.value)}
        testId="user-creation__phone"
        // Regex test for 3 digits 0-9, followed by no more than one space, period, or hyphen, then three digits, another optional space/period/hyphen, and four digits
        // We don't actually validate this field - it's not required - but the pattern is nice to have
        pattern="([0-9]{3}[\s\.-]{0,1}[0-9]{3}[\s\.-]{0,1}[0-9]{4})"
        max-length="12"
        disabled={noClient || deletedUser}
      />

      <QESTextInput containerClassName="col-3" label="SF Contact ID" value={adminUsersContext.sFContactId} onChange={(e) => adminUsersContext.setSFContactId(e.target.value)} testId="user-creation__sf-contact-id" disabled={noClient || deletedUser} />
    </div>
  );

  const booleanBlock = props.userExists && props.userIsQuestAdmin ? (
    <>
      <div className="row mb-4">
        <QESCheckbox containerClassName="col-12" label="Password Expiration Enabled" testId="user-creation__password-expiration-enabled" onChange={(e) => adminUsersContext.setPasswordExpirationEnabled(e.target.checked)} checked={adminUsersContext.passwordExpirationEnabled} disabled={noClient || deletedUser} />
      </div>
    </>
  ) : null;

  const guestLoginBlock = props.clientId === GlobalConstants.QuestClientId ? (
    <>
      <div className="row">
        <QESCheckbox containerClassName="col-12" label="Resource Admin Guest Logins Allowed" testId="user-creation__resource_admin_guest_logins_allowed" onChange={(e) => adminUsersContext.setResourceAdminGuestLoginsAllowed(e.target.checked)} checked={adminUsersContext.resourceAdminGuestLoginsAllowed} disabled={noClient || deletedUser || adminUsersContext.email.toLowerCase().endsWith('-t@questanalytics.com')} />
      </div>
      <div className="row mb-4">
        <QESCheckbox containerClassName="col-12" label='Quest Viewer Guest Logins Allowed' testId="user-creation__quest_viewer_guest_logins_allowed" onChange={(e) => adminUsersContext.setQuestViewerGuestLoginsAllowed(e.target.checked)} checked={adminUsersContext.questViewerGuestLoginsAllowed} disabled={noClient || deletedUser} />
      </div>
    </>
  ) : null;

  if (adminUsersContext.allClients.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {props.userExists ? (
        <>
          {emailAndUserBlock}
          {adminUsersContext.status == UserStatus.UNCONFIRMED ? unconfirmedEmailBlock : null}
          {usernameField}
          {booleanBlock}
          {guestLoginBlock}
          {nameBlock}
          {titleAndJobRoleBlock}
          {addressBlock}
          {phoneAndSfIdBlock}
          {informationBlock}
        </>
      ) : (
        <>
          {emailAndUserBlock}
          {guestLoginBlock}
          {nameBlock}
          {titleAndJobRoleBlock }
          {addressBlock}
          {phoneAndSfIdBlock}
        </>
      )}
    </>
  );
}